<!-- src/views/admin/AdminUserProfileView.vue -->
<template>
  <div class="p-4 sm:p-6 md:p-8 bg-[#F0F0F0] min-h-screen font-sans selection:bg-[#20FCF1]/30 text-left">
    <div class="max-w-7xl mx-auto pb-24">

      <PendingActionsPanel />

      <div v-if="!loading && userProfile" class="flex justify-end mb-8 no-print">
        <router-link :to="{ name: 'AdminPersonnelDashboard' }" class="bg-[#0A1630] text-white px-10 py-4 rounded-2xl text-[10px] font-black uppercase tracking-widest transition-all hover:bg-slate-800 shadow-xl active:scale-95 flex items-center gap-2">
          &larr; Return to Command Centre
        </router-link>
      </div>

      <div v-if="loading" class="flex flex-col items-center justify-center py-32">
        <div class="w-12 h-12 border-4 border-[#0A1630] border-t-[#20FCF1] rounded-full animate-spin"></div>
        <p class="text-[10px] font-black uppercase tracking-[0.3em] text-slate-400 mt-6">Decrypting Member Profile...</p>
      </div>
      
      <div v-else-if="error" class="bg-rose-50 border-2 border-[#EF4444]/20 p-10 rounded-[40px] text-center text-[#EF4444] font-black uppercase tracking-widest text-xs animate-fadeIn">
        {{ error }}
      </div>

      <div v-else-if="userProfile" class="space-y-10 animate-fadeIn">
        
        <!-- 1. MASTER HEADER IDENTITY CARD -->
        <div class="bg-[#0A1630] p-8 md:p-12 rounded-[40px] shadow-2xl border-b-4 border-[#20FCF1] relative overflow-hidden">
          <div class="absolute top-0 right-0 p-12 opacity-5 pointer-events-none">
            <BriefcaseIcon v-if="isSubContractor" class="h-32 w-32 text-white" />
            <UserIcon v-else class="h-32 w-32 text-white" />
          </div>
          
          <div class="relative z-10 flex flex-col xl:flex-row gap-8 justify-between items-center">
              <div class="flex items-center gap-8 text-left">
                <div class="h-24 w-24 bg-white/10 rounded-[32px] flex items-center justify-center border border-white/20 shadow-2xl overflow-hidden text-center">
                  <img v-if="userProfile?.photoUrl" :src="userProfile.photoUrl" class="h-full w-full object-cover" alt="Profile" />
                  <div v-else class="flex flex-col items-center justify-center opacity-40 text-white text-center">
                    <BriefcaseIcon v-if="isSubContractor" class="h-10 w-10" />
                    <UserIcon v-else class="h-10 w-10" />
                  </div>
                </div>
                <div class="space-y-3 text-left">
                  <div class="flex items-center gap-4 text-left">
                    <h1 class="text-3xl font-black text-white uppercase tracking-tight leading-none text-left">
                        {{ isSubContractor ? (flattenedSubData.companyInfo?.companyName || userProfile?.firstName) : (userProfile?.firstName + ' ' + userProfile?.lastName) }}
                    </h1>
                    <div class="bg-white/5 border border-white/10 px-3 py-1.5 rounded-xl flex items-center gap-2">
                        <StarIcon class="h-4 w-4 text-amber-400 fill-amber-400" />
                        <span class="text-white font-black text-sm">{{ userProfile?.averageRating?.toFixed(1) || '0.0' }}</span>
                    </div>
                  </div>
                  <div class="flex flex-wrap items-center gap-3">
                    <span class="text-[10px] font-black text-[#20FCF1] uppercase tracking-[0.2em] bg-white/5 px-3 py-1 rounded-lg border border-white/10">{{ displayJobTitle }}</span>
                    <span 
                      :class="isUserActive ? 'bg-[#20FCF1] text-[#0A1630]' : 'bg-amber-500 text-white'" 
                      class="px-4 py-1 rounded-full text-[9px] font-black uppercase tracking-widest shadow-lg"
                    >
                      Status: {{ (userProfile.status || 'pending_onboarding').replace('_', ' ') }}
                    </span>
                  </div>
                </div>
              </div>

              <div class="flex flex-wrap gap-3 justify-center no-print">
                  <!-- Secure Administrative MFA Emergency Recovery Shield -->
                  <button 
                    v-if="canManageSecurity"
                    @click="showMfaResetConfirmModal = true"
                    class="bg-amber-500/10 border border-amber-500/20 text-amber-500 px-6 py-4 rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-amber-500 hover:text-[#0A1630] transition-all shadow-lg flex items-center gap-3 animate-none"
                  >
                    <ShieldCheckIcon class="h-4 w-4 text-amber-500" /> 
                    <span>Emergency Reset MFA</span>
                  </button>

                  <!-- Authorise Registry button -->
                  <button 
                    v-if="canManageSecurity"
                    @click="showVerifyConfirmModal = true"
                    class="bg-white/5 border border-[#20FCF1]/30 text-[#20FCF1] px-6 py-4 rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-[#20FCF1] hover:text-[#0A1630] transition-all shadow-lg flex items-center gap-3"
                  >
                    <ShieldCheckIcon class="h-4 w-4" /> 
                    <span>Verify & Activate User</span>
                  </button>

                  <router-link
                    :to="{ name: 'AdminEditUser', params: { userId: userId } }"
                    class="bg-white/10 border border-white/10 text-white px-6 py-4 rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-white/20 transition-all shadow-lg flex items-center gap-3"
                  >
                    <PencilSquareIcon class="h-4 w-4" /> Edit {{ userProfile?.firstName }}'s Profile
                  </router-link>
              </div>
          </div>
        </div>

        <div class="grid grid-cols-1 lg:grid-cols-3 gap-10">
            
            <div class="lg:col-span-2 space-y-10 text-left">
                
                <!-- 2. REQUESTS MODULE -->
                <div class="bg-white p-10 rounded-[40px] shadow-2xl border border-slate-100 animate-fadeIn text-left">
                  <div class="flex items-center justify-between mb-8 border-b pb-6 text-left">
                    <h2 class="text-[11px] font-black text-[#0A1630] uppercase tracking-[0.3em] flex items-center gap-3 text-left">
                      <BellIcon class="h-5 w-5 text-[#20FCF1]" /> Requests & Compliance Tasks
                    </h2>
                    <button @click="showAssignActionModal = true" class="bg-[#0A1630] text-white px-6 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-slate-800 transition-all shadow-md">
                      + Issue Request
                    </button>
                  </div>

                  <div v-if="actions.length === 0" class="text-slate-400 text-center py-12 italic text-sm font-medium bg-slate-50/50 rounded-[32px] border border-dashed border-slate-100">
                    No active requests identified for this account.
                  </div>
                  <div v-else class="max-h-[380px] overflow-y-auto pr-2 custom-scrollbar space-y-4 text-left">
                    <div v-for="action in actions" :key="action.id" class="border border-slate-50 bg-slate-50/50 rounded-2xl p-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-6 group text-left">
                      <div class="text-left">
                        <div class="flex items-center gap-3 mb-2 text-left">
                          <h3 class="font-black text-[#0A1630] uppercase text-sm tracking-tight text-left">{{ action.title }}</h3>
                          <span :class="getPriorityClass(action.priority)" class="text-[9px] px-3 py-1 rounded-full font-black uppercase tracking-widest border">
                            {{ action.priority }} Priority
                          </span>
                        </div>
                        <p class="text-xs text-slate-500 font-medium leading-relaxed max-w-xl text-left">{{ action.description }}</p>
                      </div>
                      
                      <div class="flex items-center gap-4 shrink-0 text-left">
                        <span v-if="action.status === 'Completed'" class="text-emerald-600 font-black text-[10px] uppercase tracking-widest flex items-center gap-2">
                          <CheckCircleIcon class="h-4 w-4" /> Finalised
                        </span>
                        <span v-else class="text-amber-600 font-black text-[10px] uppercase tracking-widest flex items-center gap-2 animate-pulse">
                          <ClockIcon class="h-4 w-4" /> Pending
                        </span>
                        <button v-if="action.status === 'Completed'" @click="dismissTask(action.id)" class="text-slate-300 hover:text-[#EF4444] transition-colors bg-white p-2 rounded-xl shadow-sm border border-slate-100">
                          <XCircleIcon class="h-5 w-5" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- 3. INDIVIDUAL MEMBER CREDENTIALS -->
                <div class="bg-white p-10 rounded-[40px] shadow-2xl border border-slate-100 animate-fadeIn relative text-left">
                  <div class="flex items-center justify-between mb-10 border-b pb-6 text-left">
                    <h2 class="text-[11px] font-black text-[#0A1630] uppercase tracking-[0.3em] text-left">
                      {{ isSubContractor ? 'Supply Chain partner intelligence' : 'Individual Member Credentials' }}
                    </h2>
                    <div v-if="canManageSecurity" class="flex items-center gap-2 px-4 py-2 bg-slate-50 rounded-xl border border-slate-100 text-left">
                      <span class="w-1.5 h-1.5 bg-[#20FCF1] rounded-full animate-pulse"></span>
                      <span class="text-[8px] font-black uppercase text-slate-400 tracking-widest">Authoritative Entry Protocol Active</span>
                    </div>
                  </div>
                  
                  <div v-if="isSubContractor" class="space-y-12 text-left">
                    <div v-if="subData" class="space-y-10 text-left">
                        <div class="grid grid-cols-1 md:grid-cols-2 gap-10 text-left">
                          <div class="space-y-8 text-left">
                              <div><p class="micro-label">Company Name</p><p class="text-lg font-black text-[#0A1630] uppercase tracking-tight">{{ flattenedSubData.companyInfo?.companyName || 'N/A' }}</p></div>
                              <div><p class="micro-label">Registration ID</p><p class="text-sm font-bold text-slate-700 uppercase">Reg: {{ flattenedSubData.companyInfo?.regNumber || 'N/A' }}</p></div>
                          </div>
                          <div class="space-y-8 text-left">
                              <div><p class="micro-label">Lead Representative</p><p class="text-sm font-bold text-[#0A1630] uppercase">{{ flattenedSubData.companyInfo?.contactName || 'N/A' }}</p></div>
                              <div><p class="micro-label">Registry Email</p><p class="text-sm font-bold text-[#0A1630] uppercase text-left">{{ userProfile?.email }}</p></div>
                          </div>
                        </div>
                    </div>
                  </div>

                  <div class="space-y-10 text-left">
                    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 text-left">
                      <div class="space-y-2 text-left">
                        <p class="micro-label">Legal First Name</p>
                        <input v-model="userProfile.firstName" class="admin-inline-input" placeholder="First Name" />
                      </div>
                      <div class="space-y-2 text-left">
                        <p class="micro-label">Legal Last Name</p>
                        <input v-model="userProfile.lastName" class="admin-inline-input" placeholder="Last Name" />
                      </div>
                      <div class="space-y-2 text-left">
                        <p class="micro-label text-[#EF4444] font-black">MFA Mobile Destination</p>
                        <input v-model="userProfile.mobilePhoneNumber" class="admin-inline-input border-rose-100 focus:border-[#EF4444]" placeholder="+44..." />
                      </div>
                      <div class="space-y-2 text-left">
                        <p class="micro-label">Email Registry</p>
                        <input v-model="userProfile.email" class="admin-inline-input" placeholder="Email" />
                      </div>
                      <div class="space-y-2 text-left">
                        <p class="micro-label text-[#20FCF1] font-black">Department</p>
                        <select v-model="userProfile.department" class="admin-inline-input appearance-none cursor-pointer">
                           <option v-for="opt in departmentOptions" :key="opt.value" :value="opt.value">{{ opt.text }}</option>
                        </select>
                      </div>
                      <div class="space-y-2 text-left">
                        <p class="micro-label">Job Title</p>
                        <select v-model="userProfile.jobTitle" class="admin-inline-input appearance-none cursor-pointer">
                          <option v-for="opt in jobTitleOptions" :key="opt.value" :value="opt.value">{{ opt.text }}</option>
                        </select>
                      </div>
                      <div class="space-y-2 text-left">
                        <p class="micro-label">Sentinel ID</p>
                        <input v-model="userProfile.sentinelNumber" class="admin-inline-input" placeholder="Sentinel No" />
                      </div>
                      <div class="space-y-2 text-left">
                        <p class="micro-label">Passport Expiry</p>
                        <input type="date" v-model="userProfile.sentinelExpiryDate" class="admin-inline-input" />
                      </div>
                    </div>
                    
                    <div class="pt-8 border-t border-slate-50 flex flex-col md:flex-row items-center justify-between gap-6 text-left">
                      <div class="bg-slate-50 px-6 py-4 rounded-2xl border border-slate-100 flex items-center gap-3 max-w-lg text-left">
                        <InformationCircleIcon class="h-5 w-5 text-slate-400" />
                        <p class="text-[9px] font-bold text-slate-500 uppercase leading-tight text-left">
                          Commit these changes to synchronise the member's legal identity and MFA destination.
                        </p>
                      </div>
                      <button 
                        @click="syncMemberIdentity"
                        :disabled="isSavingIdentity"
                        class="bg-[#20FCF1] text-[#0A1630] px-10 py-5 rounded-2xl text-[10px] font-black uppercase tracking-widest shadow-xl hover:scale-105 transition-all active:scale-95 disabled:opacity-50 flex items-center gap-3"
                      >
                        <CloudArrowUpIcon class="h-4 w-4" />
                        {{ isSavingIdentity ? 'Synchronising...' : 'Authorise Identity Sync' }}
                      </button>
                    </div>
                  </div>
                </div>

                <!-- 4. ADMINISTRATIVE AUTHORISATION (GOVERNANCE) -->
                <div class="grid grid-cols-1 md:grid-cols-2 gap-10 text-left">
                    <section v-if="!isSubContractor" class="bg-white p-10 rounded-[32px] shadow-2xl border border-slate-100 text-left">
                      <div class="border-b pb-4 mb-8 text-left">
                        <h2 class="text-[11px] font-black text-[#0A1630] uppercase tracking-[0.3em] text-left">Signature Seal</h2>
                      </div>
                      <div class="flex flex-col items-center justify-center min-h-[160px] bg-slate-50/50 rounded-2xl border border-slate-100 p-6 text-center">
                        <div v-if="userProfile?.signatureDataUrl || userProfile?.signatureUrl" class="bg-white p-4 rounded-xl shadow-inner border border-slate-100 flex items-center justify-center w-full text-center">
                           <img :src="userProfile.signatureDataUrl || userProfile.signatureUrl" class="max-h-24 object-contain grayscale text-center" alt="Personnel Seal" />
                        </div>
                        <div v-else class="text-center">
                           <ExclamationTriangleIcon class="h-8 w-8 text-slate-200 mx-auto mb-2 text-center" />
                           <p class="text-[9px] font-black text-slate-300 uppercase tracking-widest text-center">No Signature Recorded</p>
                        </div>
                      </div>
                    </section>

                    <section class="bg-white p-10 rounded-[32px] shadow-2xl border border-slate-100 text-left">
                        <h2 class="text-[11px] font-black text-[#0A1630] uppercase tracking-[0.3em] mb-8 border-b pb-4 text-left">Admin Authorisation</h2>
                        
                        <div class="space-y-6 text-left">
                          <div class="text-left">
                            <label class="micro-label">Registry Status</label>
                            <!-- 🛡| STABILIZED DROPDOWN VALUES: Standardized to strictly match underscore separator formats -->
                            <select v-model="selectedStatus" class="admin-inline-input appearance-none p-4 cursor-pointer text-left">
                              <option value="active">Active</option>
                              <option value="pending_onboarding">Pending Onboarding</option>
                              <option value="pending_review">Pending Review</option>
                              <option value="requires_action">Requires Action</option>
                              <option value="suspended">Suspended</option>
                              <option value="inactive">Inactive</option>
                            </select>
                          </div>

                          <div class="text-left">
                            <label class="micro-label">System Access Tier</label>
                            <select v-model="selectedRoleId" class="admin-inline-input appearance-none p-4 cursor-pointer text-left">
                              <option value="standard">Standard Personnel</option>
                              <option value="admin">Full System Admin</option>
                              <option v-for="role in filteredRoles" :key="role.id" :value="role.id">{{ role.name }}</option>
                            </select>
                          </div>

                          <div class="grid grid-cols-1 gap-3 text-left">
                            <div class="flex items-center justify-between p-4 bg-slate-50 rounded-2xl border border-slate-100 text-left">
                              <p class="text-[10px] font-black uppercase text-[#0A1630] text-left">Manager</p>
                              <input type="checkbox" v-model="isDepartmentManager" class="h-5 w-5 rounded border-slate-300 text-[#20FCF1] focus:ring-[#20FCF1] cursor-pointer shadow-sm text-left" />
                            </div>
                            <div class="flex items-center justify-between p-4 bg-slate-50 rounded-2xl border border-slate-100 text-left">
                              <p class="text-[10px] font-black uppercase text-[#0A1630] text-left">Director</p>
                              <input type="checkbox" v-model="isDepartmentDirector" class="h-5 w-5 rounded border-slate-300 text-purple-600 focus:ring-purple-500 cursor-pointer shadow-sm text-left" />
                            </div>
                          </div>
                        </div>

                        <div class="mt-8 text-center">
                          <button @click="saveGovernance" :disabled="isSavingRoles" class="bg-[#0A1630] text-[#20FCF1] py-5 rounded-2xl w-full text-[10px] font-black uppercase tracking-widest shadow-xl hover:bg-slate-800 transition-all active:scale-95 border-b-2 border-[#20FCF1] text-center">
                            {{ isSavingRoles ? 'Syncing Policy...' : 'Authorise Governance Sync' }}
                          </button>
                        </div>
                    </section>
                </div>
            </div>

            <!-- RIGHT COLUMN: AUDIT LOG -->
            <div class="space-y-10 text-left">
                <div class="bg-[#0A1630] p-10 rounded-[40px] shadow-2xl border-b-4 border-[#20FCF1] text-left">
                    <h2 class="text-[10px] font-black text-[#20FCF1] uppercase tracking-[0.3em] mb-10 border-b border-white/10 pb-6 flex items-center gap-3 text-left">
                      <ClipboardDocumentCheckIcon class="h-6 w-6" /> Induction Record
                    </h2>
                    
                    <div class="space-y-3 text-left">
                      <div v-for="(form, index) in formCompletionStatus" :key="form.id" 
                        :class="['p-5 rounded-2xl border flex items-center justify-between transition-all group', form.isCompleted ? 'bg-[#20FCF1]/10 border-[#20FCF1]/20' : 'bg-white/5 border-white/5 opacity-50']">
                        <div class="flex items-center gap-4 min-w-0 text-left">
                          <CheckCircleIcon v-if="form.isCompleted" class="h-5 w-5 text-[#20FCF1] flex-shrink-0" />
                          <div v-else class="h-5 w-5 rounded-full border-2 border-white/20 flex-shrink-0"></div>
                          <span :class="form.isCompleted ? 'text-white' : 'text-slate-500'" class="truncate uppercase text-[10px] font-black tracking-tight pt-0.5 text-left">{{ index + 1 }}. {{ form.name }}</span>
                        </div>
                        <router-link v-if="form.isCompleted && form.submissionId" :to="{ name: 'completedForm', params: { collectionName: 'onboardingFormSubmissions', submissionId: form.submissionId } }" class="text-[#20FCF1] text-[9px] font-black uppercase hover:underline ml-4 text-left">View Audit</router-link>
                        <span v-else class="text-white/20 text-[9px] uppercase font-black tracking-widest text-left">Missing</span>
                      </div>
                    </div>
                </div>
            </div>
        </div>
      </div>

      <!-- Action Modal -->
      <div v-if="showAssignActionModal" class="fixed inset-0 z-[100] flex items-center justify-center bg-[#0A1630]/90 backdrop-blur-md px-4 animate-fadeIn">
        <div class="bg-white rounded-[40px] shadow-2xl w-full max-w-lg p-12 border border-white/10 text-left">
          <h3 class="text-2xl font-black text-[#0A1630] uppercase tracking-tight mb-8 text-left">Registry Request</h3>
          <div class="space-y-8 text-left">
            <div class="text-left"><label class="micro-label block text-left">Title</label><input v-model="newAction.title" type="text" class="w-full border-slate-200 rounded-2xl p-5 text-sm font-black bg-slate-50 focus:ring-2 focus:ring-[#20FCF1] outline-none text-left"></div>
            <div class="text-left"><label class="micro-label block text-left">Detailed Notes</label><textarea v-model="newAction.description" rows="3" class="w-full border-slate-200 rounded-2xl p-5 text-sm font-black bg-slate-50 focus:ring-2 focus:ring-[#20FCF1] outline-none text-left"></textarea></div>
            <div class="grid grid-cols-2 gap-8 text-left">
              <div class="text-left"><label class="micro-label block text-left">Priority</label><select v-model="newAction.priority" class="w-full border-slate-200 rounded-2xl p-5 text-sm font-black bg-slate-50 outline-none text-left"><option value="Low">Low</option><option value="Medium">Medium</option><option value="High">High</option></select></div>
              <div class="text-left"><label class="micro-label block text-left">Deadline</label><input v-model="newAction.dueDate" type="date" class="w-full border-slate-200 rounded-2xl p-5 text-sm font-black bg-slate-50 outline-none text-left"></div>
            </div>
          </div>
          <div class="mt-12 flex gap-4 text-center">
            <button @click="showAssignActionModal = false" class="flex-1 py-5 text-[10px] font-black uppercase text-slate-400 text-center">Discard</button>
            <button @click="assignAction" :disabled="isAssigning" class="flex-1 bg-[#20FCF1] text-[#0A1630] py-5 rounded-2xl text-[10px] font-black uppercase shadow-xl hover:scale-105 transition-all text-center">Confirm</button>
          </div>
        </div>
      </div>

      <!-- 🛡️ OPTION B: VERIFY & ACTIVATE IDENTITY BRIEFING MODAL -->
      <div v-if="showVerifyConfirmModal" class="fixed inset-0 z-[100] flex items-center justify-center bg-[#0A1630]/90 backdrop-blur-md px-4 animate-fadeIn">
        <div class="bg-white rounded-[40px] shadow-2xl w-full max-w-xl p-10 md:p-12 border-slate-100 text-left relative max-h-[90vh] overflow-y-auto custom-scrollbar">
          
          <button @click="showVerifyConfirmModal = false" class="absolute top-6 right-6 text-slate-400 hover:text-[#EF4444] transition-colors border-none bg-transparent">
            <XMarkIcon class="h-8 w-8" />
          </button>

          <h3 class="text-2xl font-black text-[#0A1630] uppercase tracking-tight mb-4 flex items-center gap-3">
            <ShieldCheckIcon class="h-6 w-6 text-[#20FCF1]" /> 
            Verify & Activate Member
          </h3>
          <p class="text-slate-400 text-xs font-bold uppercase tracking-wider mb-8">
            Sentinel Safe Registry Override
          </p>

          <div class="text-slate-500 text-xs font-semibold leading-relaxed space-y-4 mb-8 text-left">
            <p>This protocol bypasses the operative's standard onboarding sequence by executing two critical server-level handshakes:</p>
            <ul class="list-disc pl-5 space-y-2">
              <li><strong>Re-Activates Temporary Password</strong>: Re-Activates the user's temporary password if the<strong>: 7 day limit has been exceeded</strong>:, giving the user a further 7 days to use the temporary password. This feature may only be used once.</li>
              <li><strong>Forces Email Verification</strong>: Automatically marks their email as verified (<code class="bg-slate-100 px-1 py-0.5 rounded text-[#0A1630]">emailVerified: true</code>) on the Sentinel Safe Server, unlocking their native MFA capability.</li>
              <li><strong>Activates Profile Status</strong>: Instantly upgrades their registration status to <strong>'Active'</strong>, permitting immediate access to standard Timesheets and Fleet check modules.</li>
            </ul>
            <p class="pt-2 text-slate-400"><strong>When to use:</strong> Use this override ONLY when the operative's identity documents have been manually verified by HR, or when they are blocked from completing onboarding due to verification constraints.</p>
          </div>

          <div class="flex gap-4 text-center">
            <button @click="showVerifyConfirmModal = false" class="flex-1 py-4 bg-slate-100 text-slate-500 rounded-xl font-black uppercase text-[10px] tracking-widest hover:bg-slate-200 transition-all text-center">Discard</button>
            <button 
              type="button"
              @click="executeSecurityFix" 
              :disabled="isFixingSecurity"
              class="flex-1 py-4 bg-[#20FCF1] text-[#0A1630] rounded-xl font-black uppercase text-[10px] tracking-widest hover:scale-105 active:scale-95 transition-all shadow-xl disabled:opacity-50 text-center border-none"
            >
              {{ isFixingSecurity ? 'Authorising...' : 'Verify & Activate' }}
            </button>
          </div>
        </div>
      </div>

      <!-- 🛡️ OPTION B: EMERGENCY RESET MFA BRIEFING MODAL -->
      <div v-if="showMfaResetConfirmModal" class="fixed inset-0 z-[100] flex items-center justify-center bg-[#0A1630]/90 backdrop-blur-md px-4 animate-fadeIn">
        <div class="bg-white rounded-[40px] shadow-2xl w-full max-w-xl p-10 md:p-12 border-slate-100 text-left">
          
          <div class="mx-auto lg:mx-0 w-16 h-16 bg-rose-50 rounded-[24px] flex items-center justify-center mb-6 border border-rose-100 shadow-inner">
            <ShieldCheckIcon class="h-8 w-8 text-[#EF4444]" />
          </div>

          <h3 class="text-2xl font-black text-[#0A1630] uppercase tracking-tight mb-4 text-left">
            Security Briefing: Emergency MFA Reset
          </h3>
          <p class="text-[9px] font-black uppercase tracking-[0.2em] text-[#EF4444] bg-rose-50 py-1.5 px-4 rounded-lg inline-block mb-6 shadow-sm border border-rose-100">
             MFA Unenrollment & Exemption Override
          </p>

          <div class="text-slate-500 text-xs font-semibold leading-relaxed space-y-4 mb-8 text-left">
            <p>This protocol executes an administrative override of the operative's multi-factor credentials on the server:</p>
            <ul class="list-disc pl-5 space-y-2">
              <li><strong>Programmatic Unenrollment</strong>: Instantly deletes all registered SMS phone numbers and second factors on the Sentinel Safe Auth database.</li>
              <li><strong>Applies Exemption Key</strong>: Appends <code class="bg-slate-100 px-1 py-0.5 rounded text-[#0A1630]">mfaExempt: true</code> to their Sentinel Safe profile document, letting them bypass any future SMS authentication codes upon login.</li>
            </ul>
            <p class="pt-2 text-slate-400"><strong>When to use:</strong> Use this override ONLY when an operative has lost access to their registered mobile device, is experiencing carrier SMS delays, or is locked out of their active session.</p>
          </div>

          <div class="flex gap-4 text-center">
            <button @click="showMfaResetConfirmModal = false" class="flex-1 py-4 text-[10px] font-black uppercase text-slate-400 text-center border-none bg-transparent">Discard</button>
            <button 
              @click="executeMfaReset" 
              :disabled="isResettingMfa"
              class="flex-1 bg-amber-500 text-[#0A1630] py-4 rounded-xl text-[10px] font-black uppercase shadow-xl hover:scale-105 transition-all text-center border-none"
            >
              {{ isResettingMfa ? 'Resetting...' : 'Force Emergency Reset MFA' }}
            </button>
          </div>
        </div>
      </div>

    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed, onUnmounted, reactive, watch } from 'vue';
import { useRoute } from 'vue-router';
import { db, firebaseApp } from '@/firebaseConfig';
import { doc, getDoc, setDoc, collection, query, where, getDocs, onSnapshot, updateDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useUserStore } from '@/stores/userStore';
import { translateSentinelError } from '@/utils/sentinelErrorHandler';
import PendingActionsPanel from '@/components/dashboard/PendingActionsPanel.vue';
import { 
  StarIcon, CheckCircleIcon, ClockIcon, XCircleIcon, 
  UserIcon, BellIcon, ClipboardDocumentCheckIcon, 
  ExclamationTriangleIcon, ShieldCheckIcon, CloudArrowUpIcon,
  PencilSquareIcon, BriefcaseIcon, InformationCircleIcon, XMarkIcon
} from '@heroicons/vue/24/outline';

const route = useRoute();
const userStore = useUserStore();
const userProfile = ref(null);
const loading = ref(true);
const error = ref(null);
const userId = ref(''); 
const isSavingIdentity = ref(false);
const functions = getFunctions(firebaseApp, 'europe-west2');

const allRoles = ref([]);
const selectedRoleId = ref(null);
const selectedStatus = ref('active');
const isDepartmentManager = ref(false);
const isDepartmentDirector = ref(false);
const isSavingRoles = ref(false);
const jobTitleOptions = ref([]);
const departmentOptions = ref([]);

const actions = ref([]);
const showAssignActionModal = ref(false);
const isAssigning = ref(false);
const newAction = ref({ title: '', description: '', priority: 'Medium', dueDate: '' });
let unsubscribeActions = null;

const subData = ref(null);
const submissionsMap = reactive(new Map());

// Option B confirmation modals
const showVerifyConfirmModal = ref(false);
const showMfaResetConfirmModal = ref(false);

const isHydrating = ref(false);
const isFixingSecurity = ref(false);
const isResettingMfa = ref(false);

const isUserActive = computed(() => {
  return String(userProfile.value?.status || '').toLowerCase().trim() === 'active';
});

const isSubContractor = computed(() => {
  if (!userProfile.value) return false;
  const classification = String(userProfile.value.employmentClassification || userProfile.value.employmentType || '').toLowerCase().trim();
  return (classification.includes('sub') && classification.includes('contract')) || (userProfile.value.lastName || '').includes('(Entity)');
});

// Explicitly resolves categories (Employee vs Self-Employed vs Sub-Contractor) for H&S checks
const userCategory = computed(() => {
  if (!userProfile.value) return 'employee';
  const classification = String(
    userProfile.value.employmentClassification || 
    userProfile.value.employmentType || 
    ''
  ).toLowerCase().trim();
  if (classification.includes('sub') && classification.includes('contract')) return 'sub-contractor';
  if (classification.includes('self') || classification === 'cis') return 'self-employed';
  return 'employee';
});

const flattenedSubData = computed(() => {
    if (!subData.value) return {};
    const root = subData.value;
    const form = root.formData || {};
    return { companyInfo: root.companyInfo || form.companyInfo || {} };
});

const canManageSecurity = computed(() => {
  return userStore.hasAdminAccess || userStore.permissions?.canManageUsers;
});

const filteredRoles = computed(() => {
  if (!allRoles.value) return [];
  return allRoles.value.filter(role => role.id !== 'admin' && role.id !== 'standard');
});

const displayJobTitle = computed(() => {
  if (isSubContractor.value) return 'Supply Chain partner';
  const val = userProfile.value?.jobTitle;
  if (!val) return 'Registry Entry';
  const match = jobTitleOptions.value.find(o => o.value === val);
  return match ? match.text : val.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
});

/** 🛡️ COMPLIANCE ROADMAP: Form sequence and original names restored */
const baseOnboardingForms = [
  { id: 'yourDetails', name: 'Your Details' },
  { id: 'employeeInduction', name: 'Induction' }, 
  { id: 'rolesAndResponsibilities', name: 'Responsibility Acknowledgement' },
  { id: 'literacyTest', name: 'Literacy Test' },
  { id: 'annualHealthAssessment', name: 'Health Assessment' },
  { id: 'eyesightDeclaration', name: 'Eyesight Declaration' },
  { id: 'rightToWork', name: 'Right to Work' },
  { id: 'ppeRequest', name: 'PPE Issue' },
  { id: 'primarySponsorContract', name: 'Primary Sponsor Contract' },
  { id: 'employeeHandbookSignOff', name: 'Employee Handbook Acknowledgement', employeeOnly: true },
  { id: 'subContractorPack', name: 'Sub-Contractor Pack', subContractorOnly: true },
];

const formCompletionStatus = computed(() => {
  if (!userProfile.value) return [];

  const isPrimarySponsor = String(userProfile.value.sponsorshipType || '').trim().toLowerCase() === 'primary';

  const filtered = baseOnboardingForms.filter(form => {
    if (userCategory.value === 'sub-contractor') return form.subContractorOnly;
    if (form.subContractorOnly) return false;

    // Form 9 (Primary Sponsor Contract) is strictly hidden from non-primary sponsor users
    if (form.id === 'primarySponsorContract') {
      return isPrimarySponsor;
    }

    if (form.employeeOnly && userCategory.value !== 'employee') return false; // Correctly filters out Handbook for Self-Employed
    return true;
  });
  return filtered.map(f => ({ ...f, isCompleted: submissionsMap.has(f.id), submissionId: submissionsMap.get(f.id) || null }));
});

// Option B security handler
const executeSecurityFix = async () => {
  if (!userId.value) return;
  isFixingSecurity.value = true;
  try {
    const handshake = httpsCallable(functions, 'adminForceVerifyUser');
    const res = await handshake({ targetUserId: userId.value });
    if (res.data?.success) {
        await setDoc(doc(db, 'userProfiles', userId.value), { emailVerified: true, status: 'active' }, { merge: true });
        selectedStatus.value = 'active';
        showVerifyConfirmModal.value = false; // Close Option B Modal
        alert("Identity Authorised and Activated successfully.");
    }
  } catch (err) { 
      console.warn("Handshake fail", err); 
  } finally { 
      isFixingSecurity.value = false; 
  }
};

const getPriorityClass = (priority) => {
  if (priority === 'High') return 'bg-rose-50 text-[#EF4444] border-rose-100';
  if (priority === 'Medium') return 'bg-amber-50 text-amber-600 border-amber-100';
  return 'bg-blue-50 text-blue-600 border-blue-100';
};

const assignAction = async () => {
  if (!newAction.value.title) return;
  isAssigning.value = true;
  try {
    await httpsCallable(functions, 'assignUserAction')({ targetUserId: userId.value, ...newAction.value });
    showAssignActionModal.value = false;
    newAction.value = { title: '', description: '', priority: 'Medium', dueDate: '' };
  } catch (err) { console.warn("Assign fail", err); } finally { isAssigning.value = false; }
};

const dismissTask = async (actionId) => {
  if(!confirm("Authorise dismissal?")) return;
  try { await httpsCallable(functions, 'dismissAction')({ actionId }); } catch (err) { console.warn("Dismiss fail", err); }
};

const fetchRegistryOptions = async (clientId) => {
  try {
    const [titles, depts] = await Promise.all([
      getDoc(doc(db, 'formOptions', 'jobTitles')),
      getDoc(doc(db, 'formOptions', 'departments'))
    ]);
    if (titles.exists()) jobTitleOptions.value = titles.data()[clientId] || titles.data().global || [];
    if (depts.exists()) departmentOptions.value = depts.data()[clientId] || depts.data().global || [];
  } catch (e) { console.warn("Registry fetch fail", e); }
};

const syncMemberIdentity = async () => {
    isSavingIdentity.value = true;
    try {
        const updatePayload = {
            firstName: userProfile.value.firstName,
            lastName: userProfile.value.lastName,
            email: String(userProfile.value.email).toLowerCase().trim(),
            mobilePhoneNumber: userProfile.value.mobilePhoneNumber,
            phoneNumber: userProfile.value.mobilePhoneNumber, 
            department: userProfile.value.department,
            sentinelNumber: userProfile.value.sentinelNumber,
            sentinelExpiryDate: userProfile.value.sentinelExpiryDate || null,
            jobTitle: userProfile.value.jobTitle
        };

        if (isSubContractor.value) {
            // 🛡️ SUB-CONTRACTOR DIRECT WRITE: Directly updates Firestore userProfiles to avoid Firebase Auth errors
            const profileRef = doc(db, 'userProfiles', userId.value);
            await updateDoc(profileRef, updatePayload);
            alert("Registry Identity Synchronised Successfully.");
        } else {
            const handshake = httpsCallable(functions, 'adminUpdateUserRegistry');
            const res = await handshake({ targetUserId: userId.value, updateData: updatePayload });
            if (res.data?.success) { alert("Registry Identity Synchronised Successfully."); }
        }
    } catch (err) { 
        alert(translateSentinelError(err)); 
    } finally { isSavingIdentity.value = false; }
};

const saveGovernance = async () => {
    if (!selectedRoleId.value) return;
    isSavingRoles.value = true;
    try {
        // 🛡️ SUB-CONTRACTOR SECURITY PROTOCOL: Bypasses Firebase Auth setCustomUserClaims if the profile is an unauthenticated Sub-Contractor Entity
        if (!isSubContractor.value) {
            await httpsCallable(functions, 'setUserRoleAndPermissions')({ 
                userId: userId.value, 
                roleId: selectedRoleId.value,
                additionalClaims: { 
                    isDepartmentManager: isDepartmentManager.value, 
                    isDepartmentDirector: isDepartmentDirector.value, 
                    department: userProfile.value.department || ''
                }
            });
        }
        
        const handshake = httpsCallable(functions, 'adminUpdateUserRegistry');
        
        // 🛡️ STABILIZED GOVERNANCE VALUES: Normalised to lowercase underscore formats
        const normalizedStatus = String(selectedStatus.value).toLowerCase().trim().replace(/ /g, '_');
        
        if (isSubContractor.value) {
            // 🛡️ SUB-CONTRACTOR DIRECT WRITE: Directly updates Firestore to bypass Firebase Auth checks for subcontractor entities
            const profileRef = doc(db, 'userProfiles', userId.value);
            await updateDoc(profileRef, {
                status: normalizedStatus,
                isDepartmentManager: isDepartmentManager.value,
                isDepartmentDirector: isDepartmentDirector.value,
                permissionRoleId: selectedRoleId.value,
                role: (selectedRoleId.value === 'admin' || selectedRoleId.value === 'standard') ? selectedRoleId.value : 'manager'
            });
        } else {
            await handshake({
                targetUserId: userId.value,
                updateData: {
                    status: normalizedStatus,
                    isDepartmentManager: isDepartmentManager.value,
                    isDepartmentDirector: isDepartmentDirector.value,
                    permissionRoleId: selectedRoleId.value,
                    role: (selectedRoleId.value === 'admin' || selectedRoleId.value === 'standard') ? selectedRoleId.value : 'manager'
                }
            });
        }

        alert("Access Governance Policy Synchronised.");
        await initProfile(); 
    } catch(err) { alert(translateSentinelError(err)); } finally { isSavingRoles.value = false; }
};

// Option B security handler
const executeMfaReset = async () => {
  const targetId = userId.value || route.params.userId;
  if (!targetId) return;

  isResettingMfa.value = true;
  try {
    const resetMfaFunc = httpsCallable(functions, 'adminResetUserMFA');
    const res = await resetMfaFunc({ targetUserId: targetId });
    
    if (res.data && res.data.success) {
      showMfaResetConfirmModal.value = false; // Close Option B Modal
      alert("Emergency Reset Executed: SMS factors cleared, email verified on server, and user marked as exempt.");
      await initProfile();
    } else {
      throw new Error(res.data ? res.data.message : 'Registry response empty.');
    }
  } catch (err) {
    console.error("MFA Handshake Clear Failure:", err);
    alert("Emergency reset failed: " + err.message);
  } finally {
    isResettingMfa.value = false;
  }
};

const initProfile = async () => {
  if (isHydrating.value) return;
  isHydrating.value = true;
  userId.value = route.params.userId;
  if (!userId.value) { loading.value = false; isHydrating.value = false; return; }
  
  if (unsubscribeActions) unsubscribeActions();
  unsubscribeActions = onSnapshot(query(collection(db, 'user_actions'), where('targetUserId', '==', userId.value)), (snap) => {
    actions.value = snap.docs.map(d => ({ id: d.id, ...d.data() })).sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
  });

  try {
    const clientId = userStore.clientId || userStore.userProfile?.clientId;
    if (!clientId) throw new Error("Client Identity Missing.");

    // 🛡️ DIRECT FIRESTORE RETRIEVAL BYPASS: Resolves Super Admin server-side claims mismatch
    try {
      const rolesRef = collection(db, 'clients', clientId, 'permissionRoles');
      const rolesSnap = await getDocs(rolesRef);
      allRoles.value = rolesSnap.docs.map(d => ({ id: d.id, ...d.data() }));
    } catch (rolesErr) {
      console.warn("Direct Firestore roles fetch failed, trying Cloud Function fallback...", rolesErr);
      const rolesRes = await httpsCallable(functions, 'getPermissionRoles')();
      allRoles.value = rolesRes.data || [];
    }
    
    const docSnap = await getDoc(doc(db, 'userProfiles', userId.value));
    if (docSnap.exists()) {
      const data = docSnap.data();
      userProfile.value = data;
      
      // 🛡️ STABILIZED VALUE ASSIGNMENT: Normalised to lowercase underscore formats
      selectedStatus.value = String(data.status || 'pending_onboarding').toLowerCase().trim().replace(/ /g, '_');
      
      selectedRoleId.value = (data.role === 'manager' && data.permissionRoleId) ? data.permissionRoleId : (data.role || 'standard');
      isDepartmentManager.value = !!data.isDepartmentManager;
      isDepartmentDirector.value = !!data.isDepartmentDirector;
      
      if (data.clientId) await fetchRegistryOptions(data.clientId);
      
      submissionsMap.clear();
      const q = query(collection(db, "onboardingFormSubmissions"), where("userId", "==", userId.value));
      const qSnap = await getDocs(q);
      qSnap.forEach((docItem) => { if (docItem.data().formIdentifier) submissionsMap.set(docItem.data().formIdentifier, docItem.id); });

      if (isSubContractor.value) {
          const subSnap = await getDocs(query(collection(db, 'onboardingFormSubmissions'), where('userId', '==', userId.value), where('formIdentifier', '==', 'subContractorPack')));
          if (!subSnap.empty) subData.value = subSnap.docs[0].data();
      }
    }
  } catch (err) { console.warn("Hydration fail", err); } finally { loading.value = false; isHydrating.value = false; }
};

onMounted(() => { if (userStore.isAuthReady) initProfile(); });
watch(() => userStore.isAuthReady, (ready) => { if (ready) initProfile(); });
onUnmounted(() => { if (unsubscribeActions) unsubscribeActions(); });
</script>

<style scoped>
.micro-label { @apply text-[9px] font-black uppercase tracking-[0.2em] text-slate-400 block mb-1 text-left; }
.admin-inline-input { 
  @apply w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-xs font-black text-[#0A1630] uppercase outline-none focus:ring-2 focus:ring-[#20FCF1] focus:bg-white transition-all text-left;
}
.animate-fadeIn { animation: fadeIn 0.4s ease-out forwards; }
@keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
.custom-scrollbar { scrollbar-width: thin; scrollbar-color: #20FCF1 transparent; }
.custom-scrollbar::-webkit-scrollbar { width: 4px; }
.custom-scrollbar::-webkit-scrollbar-thumb { background: #20FCF1; border-radius: 10px; }
</style>